import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Type } from 'lucide-react';
import { cn } from '@/lib/utils';

// Storage key for labels
const LABELS_STORAGE_KEY = 'matterport-custom-labels';

export function Label3DOverlay({ 
  cameraPosition,
  enabled = true,
  onLabelClick,
}) {
  const [labels, setLabels] = useState([]);

  // Load labels from localStorage
  useEffect(() => {
    const loadLabels = () => {
      const saved = localStorage.getItem(LABELS_STORAGE_KEY);
      if (saved) {
        try {
          setLabels(JSON.parse(saved));
        } catch (e) {
          console.warn('Failed to parse saved labels:', e);
        }
      }
    };
    
    loadLabels();
    
    // Listen for storage changes (when labels are added/removed in sidebar)
    const handleStorage = (e) => {
      if (e.key === LABELS_STORAGE_KEY) {
        loadLabels();
      }
    };
    window.addEventListener('storage', handleStorage);
    
    // Also poll for changes since storage event doesn't fire in same tab
    const interval = setInterval(loadLabels, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  if (!enabled || labels.length === 0) {
    return null;
  }

  // Simple 3D to 2D projection (approximate)
  // This is a simplified version - real implementation would use SDK's worldToScreen
  const projectTo2D = (position3D) => {
    if (!cameraPosition?.position || !position3D) return null;
    
    // Calculate relative position from camera
    const dx = position3D.x - cameraPosition.position.x;
    const dy = position3D.y - cameraPosition.position.y;
    const dz = position3D.z - cameraPosition.position.z;
    
    // Simple distance check - only show labels within reasonable range
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (distance > 20) return null; // Too far
    
    // Very basic projection (this is approximate, not mathematically accurate)
    // Real implementation would use the SDK's coordinate transformation
    const screenX = 50 + (dx * 10); // Center + offset
    const screenY = 50 - (dy * 10) + (dz * 5); // Center + offset
    
    // Clamp to reasonable bounds
    if (screenX < 5 || screenX > 95 || screenY < 5 || screenY > 95) {
      return null;
    }
    
    return {
      x: Math.max(5, Math.min(95, screenX)),
      y: Math.max(5, Math.min(95, screenY)),
      scale: Math.max(0.5, Math.min(1.5, 10 / distance)),
      distance,
    };
  };

  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      {labels.filter(l => l.visible !== false).map((label) => {
        const pos2D = projectTo2D(label.position);
        
        // If we can't project or label is far, show at a fixed position based on stored position
        // This is a fallback - ideally we'd use SDK's worldToScreen
        const displayPos = pos2D || {
          x: 20 + (labels.indexOf(label) * 15) % 60,
          y: 20,
          scale: 0.8,
        };
        
        return (
          <div
            key={label.id}
            className="absolute pointer-events-auto transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
            style={{
              left: `${displayPos.x}%`,
              top: `${displayPos.y}%`,
              transform: `translate(-50%, -50%) scale(${displayPos.scale || 1})`,
              zIndex: pos2D ? Math.floor(100 - (pos2D.distance || 10)) : 50,
            }}
          >
            <div 
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm",
                "bg-primary/90 text-primary-foreground",
                "border border-primary-foreground/20",
                "hover:bg-primary hover:scale-105 transition-all cursor-pointer"
              )}
              onClick={() => onLabelClick && onLabelClick(label)}
            >
              <Type className="h-4 w-4" />
              <span className="text-sm font-medium whitespace-nowrap">
                {label.text}
              </span>
            </div>
            {/* Pointer/stem */}
            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-primary/90" />
          </div>
        );
      })}
    </div>
  );
}

export default Label3DOverlay;
